import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next';
import { ButtonGroup, Button, Card, CardBody, CardTitle } from "reactstrap";
import { Link } from 'react-router-dom/cjs/react-router-dom.min';
import { Log, Ticket, Tickettype, User } from '../../models';
import { Utils, Screenings } from '../../services';
import * as FileSaver from 'file-saver';
import * as XLSX from 'xlsx';
import {
    loadTickettypes,
    loadEmailLayouts,
    loadScreenings,
    importTickets,
    clearLastTicketImportLogs,
} from '../../redux';
import CSVReader from 'react-csv-reader';
import _ from 'lodash';
import moment from 'moment';

import './TicketsRawImporter.css';

const SAMPLE_DATA = [
    {
        'Nb': '1',
        'Tickettype': 'festival_pass',
        'Pricing': 'invitation',
        'Title': 'miss',
        'Firstname': 'Jane',
        'Lastname': 'Doe',
        'Email': 'jane.doe@gmail.com',
        'Street': 'Avenue d\'Aïre 56',
        'Zip': '1203',
        'City': 'Genève',
        'Country': 'Suisse',
        'Photo': 'https://fastly.picsum.photos/id/866/200/300.jpg?hmac=rcadCENKh4rD6MAp6V_ma-AyWv641M4iiOpe1RyFHeI',
        'Cellphone': '022 364 8000',
        'Sex': 'f',
        'Age': '42',
        'Language': 'CH',
        'Company': 'Net Oxygen',
        'Screening': '',
        'Row': '',
        'Seat': ''
    }
];

const screeningsCache = {};

const TicketsRawImporter = ({ visible }) => {
    const dispatch    = useDispatch();
    const { t, i18n } = useTranslation();

    const [rows, setRows]           = useState([]);
    const [tickets, setTickets]     = useState([]);
    const [fileInfos, setFileInfos] = useState([]);

    const [detailsIndex, setDetailsIndex]  = useState(0);

    const {
        isImporting,
        hasImportingError,
        importingErrorMessage
    } = useSelector(state => state.tickets);

    const emails                   = useSelector(state => state.emailLayouts.layouts)
    const { user }                 = useSelector(state => state.auth);
    const { setting }              = useSelector(state => state.settings);
    const { lastTicketImportLogs } = useSelector(state => state.logs);
    const { tickettypes }          = useSelector(state => state.tickettypes)
    const { screenings }           = useSelector(state => state.screenings)

    const ticketsImportLogs = lastTicketImportLogs?.filter(log => log.type === Log.TICKETS_IMPORT);

    useEffect(() => {
        dispatch(loadTickettypes());
        dispatch(loadEmailLayouts());
        dispatch(loadScreenings({
            start_at_gte: moment().toISOString(),
            fields: ['_id', 'pricings', 'title', 'start_at', 'stop_at']
        }));
    }, [dispatch])

    const onLoad = (rows, fileInfos) => {
        if (!rows?.length)
            return;

        // remove last empty line if any
        if (rows[rows.length - 1].length === 1 && rows[rows.length - 1][0] === '')
            rows = rows.slice(0, -1);

        setRows(rows.slice(1));
        setFileInfos(fileInfos);
        setDetailsIndex(0);
    };

    const sampleData = React.useMemo(() => {
        let types = tickettypes?.filter(type => type._id !== Tickettype.ONE_TIME_PASS);
        const screening = screenings?.find(s => {
            return !!(Object.keys(s.pricings).find(key => {
                const price = s.pricings[key].price;
                return price[Object.keys(price)[0]] === 0;
            }));
        });
        const one_time_pass = tickettypes?.find(type => type._id === Tickettype.ONE_TIME_PASS);
        if (screening && one_time_pass) {
            one_time_pass.screening_id = screening._id;
            one_time_pass.pricings     = screening.pricings;
            Object.keys(one_time_pass.pricings).forEach(key => {
                const p = one_time_pass.pricings[key].price;
                if (p[Object.keys(p)[0]] !== 0)
                    delete one_time_pass.pricings[key];
            });
            types.push(one_time_pass);
        }
        if (!types?.length)
            return SAMPLE_DATA;

        const baseRow    = _.cloneDeep(SAMPLE_DATA[0]);
        const sampleData = [];
        types.forEach(tickettype => {
            Object.keys(tickettype.pricings).forEach(pricing => {
                baseRow.Tickettype = tickettype._id;
                baseRow.Pricing    = pricing;
                if (user) {
                    baseRow.Tickettype = tickettype._id;
                    baseRow.Title      = user.gender === User.GENDER_FEMALE ? 'miss' : 'mr';
                    baseRow.Firstname  = user.firstname;
                    baseRow.Lastname   = user.lastname;
                    baseRow.Email      = user.email;
                    baseRow.Street     = user.address || baseRow.Street;
                    baseRow.Zip        = user.zip || baseRow.Zip;
                    baseRow.City       = user.city || baseRow.City;
                    baseRow.Country    = user.country || baseRow.Country;
                    baseRow.Photo      = user.avatar || baseRow.Photo;
                    baseRow.Cellphone  = user.mobile_phone || baseRow.Cellphone;
                    baseRow.Sex        = user.gender?.toLowerCase() || baseRow.Sex;
                    baseRow.Language   = i18n.language;
                }
                if (tickettype.screening_id)
                    baseRow.Screening  = tickettype.screening_id;
                sampleData.push(_.cloneDeep(baseRow));
            });
        });

        return sampleData;
    }, [ tickettypes, user, i18n.language, screenings ]);

    const generateAndDownloadCsv = () => {
        const csvData = [
            Object.keys(SAMPLE_DATA[0]).join(','),
            ...(sampleData.map(row => Object.values(row).join(',')))
        ].join('\n');
        const data = new Blob([csvData], {type: 'text/csv'});
        FileSaver.saveAs(data, 'tickets_import.csv');
    }

    const generateAndDownloadXlsx = () => {
        const fileType      = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8';
        const fileExtension = '.xlsx';

        const ws = XLSX.utils.json_to_sheet(sampleData);
        const wb = { Sheets: { 'Tickets': ws }, SheetNames: ['Tickets'] };
        const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
        const data = new Blob([excelBuffer], {type: fileType});
        FileSaver.saveAs(data, `tickets_import.${fileExtension}`);
    }

    const convertToTicket = data => {
        const ticket = {
            type: { _id: data.type },
            contact: {},
            opaque: {
                line: data.line
            }
        };

        if (data.pricing?.trim().length)
            ticket.activable_pricings = [data.pricing.trim()];

        if (data.image_url?.trim().length)
            ticket.opaque.image_url = data.image_url.trim();

        if (data.screening?.trim().length) {
            ticket.opaque.screening_id = data.screening.trim();
            if (ticket.type._id === Tickettype.ONE_TIME_PASS)
                ticket.type.screening_id = data.screening.trim();
        }

        [ 'email', 'firstname', 'lastname', 'language', 'sex', 'title', 'age', 'cellphone', 'company'].forEach(key => {
            if (data[key]?.trim().length > 0)
                ticket.contact[key] = data[key].trim();
        });

        ['zip', 'city', 'country'].forEach(key => {
            if (data[key]?.trim().length > 0) {
                ticket.contact.address = ticket.contact.address || {};
                ticket.contact.address[key] = data[key].trim();
            }
        });

        if (data.row?.trim().length && data.seat?.trim()?.length) {
            ticket.opaque.row_number = data.row.trim();
            ticket.opaque.seat_number = data.seat.trim();
        }

        return ticket;
    }

    const cancel = () => {
        setRows([]);
        dispatch(clearLastTicketImportLogs());
    };

    const doImport = () => {
        const payload = tickets?.map(convertToTicket);
        dispatch(clearLastTicketImportLogs());
        dispatch(importTickets(payload));
    };

    const { isLoading } = useSelector(state => state.tickets)

    const title = <span>{t("tickets.import.title")}</span>

    const cells = ['nb', 'type', 'pricing', 'title', 'firstname', 'lastname', 'email', 'street', 'zip', 'city', 'country', 'image_url', 'cellphone', 'sex', 'age', 'language', 'company', 'screening', 'row', 'seat', 'prefix'];

    useEffect(() => {
        const check = async() => {
            let index = 0;
            const tickets = [];
            for (const row of rows) {
                const ticket = {
                    line: index + 2
                };
                cells.forEach((property, index) => {
                    ticket[property] = row[index];
                });
                ticket.nb = parseInt(ticket.nb) || 1;
                ticket.invalidProperties = [];

                if (ticket.email?.length > 0 && !Ticket.EMAIL_REGEX.test(ticket.email))
                    ticket.invalidProperties.push('email');

                const tickettype = tickettypes?.find(t => t._id === ticket.type);
                if (!tickettype) {
                    ticket.invalidProperties.push('type');
                } else if (tickettype._id === Tickettype.ONE_TIME_PASS) {
                    if (!ticket.screening) {
                        ticket.invalidProperties.push('screening');
                    } else {
                        if (!(ticket.screening in screeningsCache)) {
                            try {
                                const result = await Screenings.get(ticket.screening, ['_id', 'title', 'start_at']);
                                screeningsCache[ticket.screening] = result.screening;
                            } catch (err) {
                                ticket.invalidProperties.push('screening');
                            }
                        }
                        if (ticket.screening in screeningsCache) {
                            const screening = screeningsCache[ticket.screening];
                            if (!(ticket.pricing in screening.pricings))
                                ticket.invalidProperties.push('pricing');
                            else {
                                const pricing = screening.pricings[ticket.pricing];
                                const price = pricing.price[Object.keys(pricing.price)[0]];
                                if (price !== 0)
                                    ticket.invalidProperties.push('pricing');
                            }
                        }
                    }
                } else if (!tickettype.pricings[ticket.pricing]) {
                    ticket.invalidProperties.push('pricing');
                }

                const nb = parseInt(row[0]);
                for (let i = 0; i < nb; i++) {
                    tickets.push(ticket);
                }

                index++;
            }

            setTickets(tickets);
        };
        check();
    }, [rows]);

    const withError = tickets?.filter(ticket => ticket.invalidProperties?.length) || [];

    const emailsToSend = React.useCallback((tickettype, pricingKey) => {
        const toSend = [];

        if (!tickettype?.opaque?.emails || !tickettype.pricings[pricingKey])
            return toSend;

        const pricing = tickettype.pricings[pricingKey];
        const isFree = pricing.price[Object.keys(pricing.price)[0]] === 0;

        const base = (pricingKey in tickettype.opaque.emails) ?
            tickettype.opaque.emails[pricingKey] :
            tickettype.opaque.emails.default;

        if (base?.creation?.length) {
            const email = emails?.find(email => email._id === base.creation);
            if (email)
                toSend.push(email);
        }

        if (isFree && base?.activation?.length) {
            const email = emails?.find(email => email._id === base.activation);
            if (email)
                toSend.push(email);
        }

        return toSend;
    }, [emails]);

    const renderErrors = React.useMemo(() => {
        return (
            <Card className="mt-2 w-100">
                <CardBody>
                    {withError.map((ticket, index) => (
                    <div key={index}>
                        <b>
                            <i className="fa fa-warning" />&nbsp;
                            { t('tickets.import.line_x', {line: ticket.line}) }
                        </b>
                        { ticket.invalidProperties.map(field => (
                        <div className="ml-5" key={ field }>
                            <i className="fa fa-arrow-right" />&nbsp;
                            { t('tickets.import.field_is_invalid', { field }) }
                        </div>
                        ))}
                    </div>
                    ))}
                </CardBody>
            </Card>
        );
    }, [withError]);

    const renderResume = React.useMemo(() => {
        const emails       = {};
        const byTickettype = {};

        if (!tickets?.length)
            return null;

        tickets.forEach(ticket => {
            const tickettype = tickettypes?.find(t => t._id === ticket.type);
            if (!tickettype)
                return;

            let pricing = tickettype.pricings[ticket.pricing];
            if (tickettype._id === Tickettype.ONE_TIME_PASS) {
                const screening = screeningsCache[ticket.screening];
                if (!screening)
                    return;
                pricing = screening.pricings[ticket.pricing];
            }

            if (!pricing)
                return;

            if (!(tickettype._id in byTickettype))
                byTickettype[tickettype._id] = {
                    name: tickettype.name[i18n.language],
                    total: 0,
                    pricings: {}
                };

            if (!(ticket.pricing in byTickettype[tickettype._id].pricings))
                byTickettype[tickettype._id].pricings[ticket.pricing] = {
                    name: pricing.name[i18n.language],
                    total: 0,
                    emails: {}
                };

            byTickettype[tickettype._id].total += 1;
            byTickettype[tickettype._id].pricings[ticket.pricing].total += 1;

            if (!ticket.email?.length)
                return;

            const emails = emailsToSend(tickettype, ticket.pricing);
            if (emails?.length) {
                emails.forEach(email => {
                    if (!(email._id in byTickettype[tickettype._id].pricings[ticket.pricing].emails))
                        byTickettype[tickettype._id].pricings[ticket.pricing].emails[email._id] = {
                            _id: email._id,
                            name: email.name[i18n.language],
                            total: 0
                        };

                    byTickettype[tickettype._id].pricings[ticket.pricing].emails[email._id].total += 1;
                });
            }
        });

        return (
            <Card className="mt-2 w-100">
                <CardBody>
                    <CardTitle>{t('tickets.import.resume_view')}</CardTitle>
                    {Object.values(byTickettype).map((type, index) => (
                        <div key={ index } className="mt-2">
                            <b>
                                <i className="fa fa-tags" />&nbsp;
                                { t('tickets.import.x_tickets_of_type', { count: type.total, name: type.name }) }
                            </b>
                            <div>
                                { Object.values(type.pricings).map((pricing, index) => (
                                <div className="ml-5" key={ index }>
                                    <i className="fa fa-usd" />&nbsp;
                                    { t('tickets.import.x_tickets_of_price', { count: pricing.total, name: pricing.name }) }
                                    <div>
                                        { Object.values(pricing.emails).map((email, index) => (
                                        <div key={ index } className="ml-5">
                                            <i className="fa fa-envelope-o" />&nbsp;
                                            { t('tickets.import.send_x_emails', { count: email.total }) }&nbsp;
                                            <Button tag={Link} color="dark" size="xs" to={`/email_layouts/edit/${email._id}`}>
                                                <i className="fa fa-link" />&nbsp;
                                                { email.name }
                                            </Button>
                                        </div>
                                        ))}
                                    </div>
                                </div>
                                ))}
                            </div>
                        </div>
                    ))}
                </CardBody>
            </Card>
        );
    }, [tickets]);

    const previewData = React.useMemo(() => {
        if (!tickets?.length)
            return null;

        if (detailsIndex > tickets?.length - 1) {
            setDetailsIndex(0);
            return null;
        }

        const ticket = convertToTicket(tickets[detailsIndex]);
        return (
            <Card className="mt-2 w-100">
                <CardBody>
                    <div className="d-flex gap-2 mb-2 justify-end">
                        <Button color="info" size="sm" disabled={detailsIndex === 0} onClick={() => setDetailsIndex(detailsIndex - 1)}>
                            <i className="fa fa-arrow-left" />
                        </Button>
                        <Button color="info" size="sm" onClick={() => setDetailsIndex(detailsIndex + 1)}>
                            <i className="fa fa-arrow-right" />
                        </Button>
                    </div>
                    <table className="table w-100">
                        <tbody>
                            <tr>
                                <th className="text-right" width="150px">{t('tickets.import.columns.number')}</th>
                                <td className="text-left">{ detailsIndex + 1 }</td>
                            </tr>
                            <tr>
                                <th className="text-right" width="150px">{t('tickets.import.columns.line')}</th>
                                <td className="text-left">{ ticket.opaque.line }</td>
                            </tr>
                            <tr>
                                <th className="text-right" width="150px">{t('tickets.import.columns.type')}</th>
                                <td className="text-left">{ tickettypes?.find(type => type._id === ticket.type._id)?.name[i18n.language] }</td>
                            </tr>

                            { ticket.activable_pricings?.length > 0 && (
                            <tr>
                                <th className="text-right" width="150px">{t('tickets.import.columns.pricing')}</th>
                                <td className="text-left">{ tickettypes?.find(type => type._id === ticket.type._id)?.pricings[ticket.activable_pricings[0]]?.name[i18n.language] }</td>
                            </tr>
                            )}

                            {[ 'email', 'firstname', 'lastname', 'language', 'sex', 'title', 'age', 'cellphone', 'company'].map(key => (
                                <tr key={ key }>
                                    <th className="text-right" width="150px">{t(`tickets.import.columns.${key}`)}</th>
                                    <td className="text-left">{ ticket.contact[key] }</td>
                                </tr>
                            ))}
                            {['zip', 'city', 'country'].map(key => (
                                <tr key={ key }>
                                    <th className="text-right" width="150px">{t(`tickets.import.columns.${key}`)}</th>
                                    <td className="text-left">{ (ticket.contact?.address ?? {})[key] }</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    <div className="d-flex gap-2 mt-2 justify-end">
                        <Button color="info" size="sm" disabled={detailsIndex === 0} onClick={() => setDetailsIndex(detailsIndex - 1)}>
                            <i className="fa fa-arrow-left" />
                        </Button>
                        <Button color="info" size="sm" onClick={() => setDetailsIndex(detailsIndex + 1)}>
                            <i className="fa fa-arrow-right" />
                        </Button>
                    </div>
                </CardBody>
            </Card>
        );
    }, [tickets, detailsIndex]);

    const ticketInfo = ticket => {
        const fullname = [
            ticket.contact?.firstname,
            ticket.contact?.lastname
        ].filter(Boolean).join(' ').trim();
        return [
            (ticket.type?.name || {})[i18n.language],
            fullname.length > 0 ? fullname : ticket.contact?.email
        ].map(s => s?.trim()).filter(Boolean).join(' - ');
    }

    const importResume = React.useMemo(() => log => (
        <div key={log._id}>
            <div className="bg-dark rounded w-100 p-2">
                <h5>
                    { t('tickets.import.resume') }
                </h5>
                <ul>
                    <li>
                        <b>{ t('tickets.import.nb_inserted', { count: log.inserted?.length }) }</b>
                    </li>
                    <li>
                        <b>{ t('tickets.import.nb_activated', { count: log.activated?.length }) }</b>
                    </li>
                    <li>
                        <b>{ t('tickets.import.nb_updated', { count: log.updated?.length }) }</b>
                    </li>
                    <li>
                        <b>{ t('tickets.import.nb_invalid', { count: log.invalid?.length }) }</b>
                    </li>
                </ul>
            </div>
            <Button onClick={ cancel } color="warning" title={t("tickets.import.do_another_import")} className="mt-2">
                <i className="fa fa-arrow-left"></i>&nbsp;{t('tickets.import.do_another_import')}
            </Button>
            {(log.activated?.length > 0) && (
                <div className="bg-info mt-2 p-2">
                    <h5>
                        { t('tickets.import.activated', { count: log.activated.length }) }
                    </h5>
                    <ul>
                    {log.activated.map(ticket => (
                        <li key={ticket._id} className="ticket">
                            <a target="_blank" href={`${setting?.eshop?.uri}/ticket/view/?uuid=${ticket._id}`}>
                                <b>
                                    { ticketInfo(ticket) }
                                </b>
                            </a>
                        </li>
                    ))}
                    </ul>
                </div>
            )}
            {(log.inserted?.length > 0) && (
                <div className="bg-info mt-2 p-2">
                    <h5>
                        { t('tickets.import.inserted', { count: log.inserted.length }) }
                    </h5>
                    <ul>
                    {log.inserted.map(ticket => (
                        <li key={ticket._id} className="ticket">
                            <a target="_blank" href={`${setting?.eshop?.uri}/ticket/view/?uuid=${ticket._id}`}>
                                <b>
                                    { ticketInfo(ticket) }
                                </b>
                            </a>
                        </li>
                    ))}
                    </ul>
                </div>
            )}
            {(log.updated?.length > 0) && (
                <div className="bg-info mt-2 p-2">
                    <h5>
                        { t('tickets.import.updated', { count: log.updated.length }) }
                    </h5>
                    <ul>
                    {log.updated.map(ticket => (
                        <li key={ticket._id} className="ticket">
                            <a target="_blank" href={`${setting?.eshop?.uri}/ticket/view/?uuid=${ticket._id}`}>
                                <b>
                                    { ticketInfo(ticket) }
                                </b>
                            </a>
                        </li>
                    ))}
                    </ul>
                </div>
            )}
            {(log.invalid?.length > 0) && (
                <div className="bg-danger mt-2 p-2">
                    <h5>
                        { t('tickets.import.invalid', { count: log.invalid.length }) }
                    </h5>
                    {log.invalid.map((ticket, i) => (
                        <div key={i} className="ticket">
                            {ticket._id ? (
                                <a target="_blank" href={`${setting?.eshop?.uri}/ticket/view/?uuid=${ticket._id}`}>
                                    <b>
                                        { ticketInfo(ticket) }
                                    </b>
                                </a>
                            ) : (
                                <b>
                                    { ticketInfo(ticket) }
                                </b>
                            )}
                            - { ticket._id }
                            <ul>
                                {Object.keys(ticket.invalid).map(key => (
                                    <li key={key}>
                                        <b>{ key }</b> : { ticket.invalid[key] }
                                    </li>
                                ))}
                            </ul>
                        </div>
                    ))}
                </div>
            )}
        </div>
    ), [ setting ]);


    if (!visible)
        return null;

    return (
        <div className="TicketsImport">
            {(isImporting) ? (
                <div className="d-flex justify-center">
                    <i className="fa fa-spinner fa-spin fa-3x fa-fw"></i>
                </div>
            ) : (
                <div>
                    {hasImportingError && (
                        <div className="mb-2 bg-danger p-2 rounded">
                            { importingErrorMessage || t('tickets.an_error_occured') }
                        </div>
                    )}

                    {ticketsImportLogs?.length > 0 ? (
                        <div>
                            { ticketsImportLogs?.map(log => importResume(log)) }
                            <Button onClick={ cancel } color="warning" title={t("tickets.import.do_another_import")} className="mt-2">
                                <i className="fa fa-arrow-left"></i>&nbsp;{t('tickets.import.do_another_import')}
                            </Button>
                        </div>
                    ) : (
                        <div>
                            {(withError?.length > 0 || !tickets?.length) && (
                            <div className="d-flex column">
                                <div className="mb-2 w-100">
                                    <small className="w-100">
                                        { t('tickets.import.desc') }
                                    </small>
                                </div>
                                <div className="mb-2 w-100">
                                    <Button size="xs" color="dark" onClick={ generateAndDownloadCsv }>
                                        <i className="fa fa-download" />&nbsp;
                                        { t('tickets.import.download_csv_file') }
                                    </Button>
                                    { /* }
                                    <Button size="xs" color="dark" onClick={ generateAndDownloadXlsx }>
                                        <i className="fa fa-download" />&nbsp;
                                        { t('tickets.import.download_xlsx_file') }
                                    </Button>
                                    { */ }
                                </div>
                                <Card className="mt-2 w-100">
                                    <CardBody>
                                        <CSVReader
                                            label={t('tickets.import.choose_csv_file')}
                                            parserOptions={{ header: false }}
                                            onFileLoaded={ onLoad }
                                            cssInputClass="form-control"
                                        />
                                    </CardBody>
                                </Card>
                            </div>
                            )}

                            {withError?.length > 0 && (
                                <div className="d-flex column">
                                    <h4>
                                        <br /><br />
                                        <i className="fa fa-warning" />&nbsp;
                                        {t('tickets.import.file_errors', {nb: withError.length})}<br />
                                        <small><i>{t('tickets.import.file_errors_desc')}</i></small>
                                    </h4>
                                    { renderErrors }
                                </div>
                            )}

                            {(tickets?.length > 0) && (!withError?.length) && (
                                <div className="d-flex column">
                                    <h4>
                                        <i className="fa fa-check" />&nbsp;
                                        {t('tickets.import.file_goods', {nb: tickets.length})}<br />
                                    </h4>
                                    <ButtonGroup className="mt-3 mb-3">
                                        <Button onClick={ cancel } color="danger" title={t("common.cancel")}>
                                            <i className="fa fa-times"></i>&nbsp;{t('common.cancel')}
                                        </Button>
                                        <Button onClick={ doImport } color="info" title={t("tickets.import.do_import")}>
                                            <i className="fa fa-upload"></i>&nbsp;{t('tickets.import.do_import')}
                                        </Button>
                                    </ButtonGroup>

                                    { renderResume }
                                    { previewData }

                                    <ButtonGroup className="mt-3 mb-3">
                                        <Button onClick={ cancel } color="danger" title={t("common.cancel")}>
                                            <i className="fa fa-times"></i>&nbsp;{t('common.cancel')}
                                        </Button>
                                        <Button onClick={ doImport } color="info" title={t("tickets.import.do_import")}>
                                            <i className="fa fa-upload"></i>&nbsp;{t('tickets.import.do_import')}
                                        </Button>
                                    </ButtonGroup>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

export default TicketsRawImporter;
